import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { CarrierService } from '@carrier/services';

@Component({
  selector: 'z-carrier-lookup',
  templateUrl: './carrier-lookup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class CarrierLookupComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  @Input() fieldName: string;

  @Input() label: string;
  @Input() placeholder: string;
  @Input() hint: string;

  @Input() hideLabel: boolean = false;

  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = true;

  @Input() clearButton: boolean = true;
  @Input() manualInput: boolean = true;

  @Input() enableDropdown: boolean = true;
  @Input() showDropdownFilter: boolean = true;
  @Input() doubleClickToOpenPopup: boolean = true;

  @Input() dropdownFilterBy: string = 'column1';

  @Output() selectedOptionChange = new EventEmitter<any>();
  @Output() change = new EventEmitter<string>();
  @Output() clear = new EventEmitter<any>();

  useDefaultLabel: boolean = false;
  useDefaultPlaceholder: boolean = false;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  carrierList$ = this.carrierService.carrierDropdownList$;

  constructor(private translocoService: TranslocoService, private carrierService: CarrierService, public formGroup: FormGroupDirective) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.hideLabel && !this.label) {
          this.useDefaultLabel = true;
          this.label = this.translate('core.components.lookups.carrier_lookup.label');
        }

        if (!this.placeholder) {
          this.useDefaultPlaceholder = true;
          this.placeholder = this.translate('core.components.lookups.carrier_lookup.placeholder');
        }
      });

    this.translocoService.events$
      .pipe(
        filter((event) => event.type === 'langChanged'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onLanguageChange();
      });

    this.carrierService.requestLists();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  onLanguageChange() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.hideLabel && this.useDefaultLabel) {
          this.label = this.translate('core.components.lookups.carrier_lookup.label');
        }

        if (this.useDefaultPlaceholder) {
          this.placeholder = this.translate('core.components.lookups.carrier_lookup.placeholder');
        }
      });
  }

  openDialog = () => {
    const lookupDialogRef = this.carrierService.carrierLookupDialog();

    lookupDialogRef.onClose.pipe(take(1)).subscribe((result) => {
      if (result !== undefined) {
        this.formGroup.form.get(this.fieldName).setValue(result.carrier);
      }
    });
  };

  onChangeHandler(value: string) {
    this.change.emit(value);
  }

  onSelectedOptionChangeHandler(event: any) {
    this.selectedOptionChange.emit(event);
  }

  onClearHandler(_event: any) {
    this.clear.emit();
  }
}
